import useAuthenticated from 'common/hooks/useAuthenticated'
import useHasMounted from 'common/hooks/useHasMounted'
import { getLocalStorage } from 'common/utils/localstorage'
import { FeatureKey } from 'common/constants/permission'
import { ACCESS_TOKEN_KEY } from 'modules/auth/constants'
import { decodeAccessToken } from 'modules/auth/utils/jwt'
import { useUserInfo } from 'modules/user/api/queries'
import { useFirstAllowedDashboardPath, usePermitByFeature } from 'modules/user/api/selector'
import { Role } from 'modules/user/constants'
import { useState, ReactNode, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { PATH_AUTH, PATH_DASHBOARD } from 'routes/paths'

type AuthGuardProps = {
  children: ReactNode
}

export default function AuthGuard({ children }: AuthGuardProps) {
  const authenticated = useAuthenticated()
  const hasMounted = useHasMounted()
  const [requestedLocation, setRequestedLocation] = useState<string | null>(null)
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const { isLoading: isUserInfoLoading } = useUserInfo()
  const canReadPond = usePermitByFeature(FeatureKey.Pond)
  const firstAllowedPath = useFirstAllowedDashboardPath()

  useEffect(() => {
    if (!authenticated) {
      if (pathname !== requestedLocation) {
        setRequestedLocation(pathname)
      }
      navigate(PATH_AUTH.login)
      return
    }

    if (pathname === PATH_DASHBOARD.root) {
      // Permissions are still loading - wait rather than redirecting on an
      // empty permission list, which would always fall through to the
      // shrimp-price fallback.
      if (isUserInfoLoading) return

      // Read the token at call time: a module-level snapshot is undefined
      // when the bundle loaded before login (SPA login never reloads the
      // page), which made jwt-decode throw on back-navigation to /dashboard.
      try {
        const decoded = decodeAccessToken(getLocalStorage(ACCESS_TOKEN_KEY))
        const prefersPondRoot = decoded.role === Role.PrimaryOwner || decoded.role === Role.CycleCaretaker
        if (prefersPondRoot && canReadPond) {
          navigate(PATH_DASHBOARD.ponds.root)
          return
        }
      } catch (error) {
        // fall through to the permission-based landing page below
      }
      // DOF: a role can lack permission for its usual landing page (e.g. an
      // employee only granted IoT access) - send them to the first section
      // they can actually read instead of a page that just shows "no
      // permission".
      navigate(firstAllowedPath)
    }
  }, [authenticated, pathname, isUserInfoLoading, canReadPond, firstAllowedPath])

  if (!hasMounted || !authenticated) {
    return null
  }

  return <>{children}</>
}
